import React, { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';

const AuthContext = createContext();

// Role-based permissions
const ROLE_PERMISSIONS = {
  super_admin: [
    'dashboard.view',
    'tenants.view', 'tenants.create', 'tenants.edit', 'tenants.delete',
    'users.view', 'users.create', 'users.edit', 'users.delete',
    'payments.view', 'payments.manage',
    'system.logs', 'system.errors',
    'reports.all',
  ],
  admin: [
    'dashboard.view', 'dashboard.hotel',
    'rooms.view', 'rooms.create', 'rooms.edit', 'rooms.delete',
    'room_types.view', 'room_types.create', 'room_types.edit', 'room_types.delete',
    'guests.view', 'guests.create', 'guests.edit', 'guests.delete',
    'reservations.view', 'reservations.create', 'reservations.edit', 'reservations.delete',
    'payments.view', 'payments.manage',
    'employees.view', 'employees.create', 'employees.edit', 'employees.delete',
    'payroll.view', 'payroll.manage',
    'reports.hotel',
  ],
  staff: [
    'dashboard.personal',
    'reservations.view', 'reservations.create', 'reservations.edit',
    'guests.view', 'guests.create', 'guests.edit',
    'rooms.view',
    'tasks.view', 'tasks.manage',
    'schedule.view',
  ],
};

// Employee types
const EMPLOYEE_TYPES = {
  front_desk: ['dashboard.view', 'reservations.view', 'reservations.create', 'reservations.edit', 'guests.view', 'guests.create', 'guests.edit', 'rooms.view'],
  housekeeping: ['dashboard.view', 'rooms.view', 'tasks.view', 'tasks.manage', 'schedule.view'],
  maintenance: ['dashboard.view', 'rooms.view', 'tasks.view', 'tasks.manage', 'schedule.view'],
  management: ['dashboard.view', 'dashboard.hotel', 'rooms.view', 'room_types.view', 'guests.view', 'reservations.view', 'employees.view', 'reports.hotel'],
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // Check authentication on mount
  useEffect(() => {
    const token = localStorage.getItem('authToken');
    const userData = localStorage.getItem('userData');

    if (token && userData) {
      try {
        const parsedUser = JSON.parse(userData);
        setUser(parsedUser);
        axios.get(`${API_URL}/tenants/current`, {
          headers: { Authorization: `Bearer ${token}` },
        }).then(({ data }) => {
          const enabledFeatures = data.data?.enabled_features || [];
          const refreshedUser = { ...parsedUser, enabledFeatures };
          localStorage.setItem('userData', JSON.stringify(refreshedUser));
          setUser(refreshedUser);
        }).catch(() => {});
      } catch (error) {
        localStorage.removeItem('authToken');
        localStorage.removeItem('userData');
      }
    }
    setLoading(false);
  }, []);

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API_URL}/auth/login`, { email, password });
      const { user: apiUser, tenant, tokens } = response.data.data;

      // Map backend field names to frontend camelCase
      const userData = buildUserData(apiUser, tenant);

      localStorage.setItem('authToken', tokens.accessToken);
      localStorage.setItem('userData', JSON.stringify(userData));
      setUser(userData);

      return { success: true, user: userData };
    } catch (error) {
      const message = error.response?.data?.message || error.message || 'Login failed';
      return { success: false, error: message };
    }
  };

  const register = async (registrationData) => {
    try {
      const response = await axios.post(`${API_URL}/auth/register`, registrationData);
      const { user: apiUser, tenant, tokens } = response.data.data;

      // Map backend field names to frontend camelCase
      const userData = buildUserData(apiUser, tenant);

      localStorage.setItem('authToken', tokens.accessToken);
      localStorage.setItem('userData', JSON.stringify(userData));
      setUser(userData);

      return { success: true, user: userData, data: response.data.data };
    } catch (error) {
      const message = error.response?.data?.message || error.response?.data?.error || error.message || 'Registration failed';
      return { success: false, error: message };
    }
  };

  const buildUserData = (apiUser, tenant) => {
    // Map specific employee roles (front_desk, housekeeping, etc.) to the generic 'staff'
    // role used by the frontend RBAC system. Keep the original role as employeeRole.
    const employeeRoles = ['front_desk', 'receptionist', 'housekeeping', 'housekeeper', 'maintenance', 'maintenance_staff', 'management', 'manager', 'accountant'];
    const effectiveRole = employeeRoles.includes(apiUser.role) ? 'staff' : apiUser.role;

    return {
      id: apiUser.id,
      email: apiUser.email,
      firstName: apiUser.first_name,
      lastName: apiUser.last_name,
      role: effectiveRole,
      employeeRole: apiUser.role,
      tenantId: tenant?.id || null,
      tenantName: tenant?.name || null,
      tenantSubdomain: tenant?.subdomain || null,
      enabledFeatures: tenant?.enabled_features || [],
      permissions: ROLE_PERMISSIONS[effectiveRole] || [],
    };
  };

  const logout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('userData');
    localStorage.removeItem('impersonating');
    localStorage.removeItem('originalToken');
    localStorage.removeItem('originalUserData');
    setUser(null);
    navigate('/login');
  };

  const startImpersonation = async (userId) => {
    try {
      const response = await axios.post(`${API_URL}/auth/impersonate`, { userId }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('authToken')}` },
      });
      const { token, user: apiUser } = response.data.data;

      // Save the super admin session so it can be restored
      localStorage.setItem('originalToken', localStorage.getItem('authToken'));
      localStorage.setItem('originalUserData', localStorage.getItem('userData'));
      localStorage.setItem('impersonating', 'true');

      const userData = buildUserData(apiUser, apiUser.tenant);
      userData.isImpersonating = true;
      userData.originalEmail = apiUser.email;

      localStorage.setItem('authToken', token);
      localStorage.setItem('userData', JSON.stringify(userData));
      setUser(userData);

      return { success: true, user: userData };
    } catch (error) {
      const message = error.response?.data?.error || error.message || 'Impersonation failed';
      return { success: false, error: message };
    }
  };

  const stopImpersonation = () => {
    const originalToken = localStorage.getItem('originalToken');
    const originalUserData = localStorage.getItem('originalUserData');

    if (originalToken && originalUserData) {
      localStorage.setItem('authToken', originalToken);
      localStorage.setItem('userData', originalUserData);
      localStorage.removeItem('impersonating');
      localStorage.removeItem('originalToken');
      localStorage.removeItem('originalUserData');
      setUser(JSON.parse(originalUserData));
      navigate('/super-admin');
    } else {
      logout();
    }
  };

  const hasPermission = (permission) => {
    if (!user) { return false; }
    return user.permissions?.includes(permission) || false;
  };

  const hasFeature = (feature) => {
    if (!user) { return false; }
    return user.enabledFeatures?.includes(feature) || false;
  };

  const hasRole = (role) => {
    if (!user) { return false; }
    return user.role === role;
  };

  const canAccess = (resource, action = 'view') => {
    const permission = `${resource}.${action}`;
    return hasPermission(permission);
  };

  const getRoleBasedRoutes = () => {
    if (!user) { return []; }

    const baseRoutes = [
      { path: '/dashboard', name: 'Dashboard' },
    ];

    const roleRoutes = {
      super_admin: [
        { path: '/super-admin', name: 'Dashboard' },
        { path: '/tenants', name: 'Tenant Management' },
        { path: '/system-payments', name: 'Payment Tracking' },
        { path: '/system-users', name: 'User Management' },
        { path: '/security-events', name: 'Security Events' },
        { path: '/announcements', name: 'Announcements' },
        { path: '/plans', name: 'Plans & Features' },
        { path: '/reports', name: 'Reports & Exports' },
        { path: '/system-health', name: 'System Health' },
        { path: '/system-logs', name: 'System Logs' },
        { path: '/calendar', name: 'Calendar' },
      ],
      admin: [
        { path: '/dashboard', name: 'Dashboard' },
        { path: '/bookings', name: 'Bookings' },
        { path: '/rooms', name: 'Rooms' },
        { path: '/guests', name: 'Guests' },
        { path: '/upcoming-arrivals', name: 'Upcoming Arrivals' },
        { path: '/employees', name: 'Employees' },
        { path: '/payroll', name: 'Payroll', feature: 'payroll' },
        { path: '/hotel-payments', name: 'Payments', feature: 'payments' },
        { path: '/pricing', name: 'Pricing', feature: 'seasonal_pricing' },
        { path: '/accounting', name: 'Accounting', feature: 'accounting' },
        { path: '/hotel-reports', name: 'Reports', feature: 'reports' },
        { path: '/attendance', name: 'Attendance & Schedule' },
        { path: '/calendar', name: 'Calendar' },
        { path: '/settings', name: 'Settings' },
      ],
      staff: [
        { path: '/employee-dashboard', name: 'Dashboard' },
        { path: '/bookings', name: 'Bookings' },
        { path: '/rooms', name: 'Rooms' },
        { path: '/guests', name: 'Guests' },
        { path: '/upcoming-arrivals', name: 'Upcoming Arrivals' },
        { path: '/tasks', name: 'Housekeeping' },
        { path: '/schedule', name: 'Schedule' },
        { path: '/calendar', name: 'Calendar' },
        { path: '/profile', name: 'Profile' },
      ],
    };

    return (roleRoutes[user.role] || baseRoutes).filter(route => !route.feature || hasFeature(route.feature));
  };

  const value = {
    user,
    loading,
    login,
    register,
    logout,
    startImpersonation,
    stopImpersonation,
    hasPermission,
    hasFeature,
    hasRole,
    canAccess,
    getRoleBasedRoutes,
    ROLE_PERMISSIONS,
    EMPLOYEE_TYPES,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default AuthContext;
